import type { Customer, Company } from "types";

export const getOwner = (customer: Customer, company: Company) => {
  if (!customer) {
    return {
      id: company.id,
      title: company.name,
      image: company.logo,
      type: "company",
    };
  }

  if (customer.first_name && customer.last_name) {
    return {
      id: customer.id,
      title: `${customer.first_name} ${customer.last_name}`,
      image: customer.avatar === null ? "" : customer.avatar,
      type: "customer",
    };
  }

  return {
    id: customer.id,
    title: customer.email,
    image: "",
    type: "customer",
  };
};

export interface PersonLike {
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}

/**
 * VERA-304 — the ONE place that decides which line of a person row is the name
 * and which is the supporting detail.
 *
 * A person with no name HAS no name. Never stand a placeholder in for one: an
 * "Unknown" over `spencer@usevergence.com` claims the system knows something it
 * does not, and it pushes the row's only real identifier down into secondary
 * type. So when there is no name the EMAIL is promoted to the primary line and
 * there is no secondary line — the row loses a line rather than gaining a lie.
 *
 * The logic already existed, correctly, in three places (`UserBlock`,
 * `UserAvatarGroup`, the owner detail header) and was defeated in all three by
 * the server fabricating `name: 'Unknown'` (fixed in `customer-data.aggregate.ts`).
 * Extracted here on the fourth call site per working-conventions §36 rather than
 * hand-rolled a fourth time.
 *
 * ⚠️ `name` can be `''`, not just null — so callers testing it directly must use
 * `||`, never `??`, which does not fall through on an empty string.
 *
 * This is deliberately NOT `getNameOrEmailSlug` below. That one returns the email's
 * LOCAL PART as a stand-in name (`spencer` for `spencer@usevergence.com`), which is
 * the same class of invention in a quieter register — it is why the API's welcome
 * mail once read "Welcome, spencer+new!". Its remaining callers (scan rows, the
 * timeline, the map) are outside this change; prefer this resolver for anything new.
 */
export const getPersonLabel = (
  person: PersonLike | null | undefined
): { primary: string | null; secondary: string | null; hasName: boolean } => {
  const composed = [person?.first_name, person?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  const name = (person?.name || "").trim() || composed;
  const email = (person?.email || "").trim() || null;

  if (name) return { primary: name, secondary: email, hasName: true };
  return { primary: email, secondary: null, hasName: false };
};

export const getNameOrEmailSlug = (
  item:
    | { first_name: string | null; last_name: string | null; email: string }
    | { name: string; email: string }
    | undefined
) => {
  if (!item) return null;

  // Handle objects with a 'name' property (like Owner)
  if ('name' in item && item.name) {
    return item.name;
  }

  // Handle objects with first_name/last_name (like Customer)
  if ('first_name' in item) {
    if (item?.first_name === null) {
      // email can be null (e.g. an anonymous scan's user object) — don't crash on .split.
      return item.email ? item.email.split("@")[0] : null;
    }
    return `${item?.first_name} ${item?.last_name}`;
  }

  return item.email ? item.email.split("@")[0] : null;
};

/**
 * VRG-607 — this deliberately does NOT import the shared `userFacingMessage`
 * sanitiser from `@repo/ui`, and that is not an oversight.
 *
 * It did, for about an hour, and it took the whole platform app down with a
 * blank screen:
 *
 *     Uncaught ReferenceError: Cannot access 'API_BASE_URL' before
 *     initialization — at signupApi.ts:12
 *
 * `signupApi.ts` reads `API_BASE_URL` from the `utils` BARREL, and this file is
 * part of that barrel. Importing `@repo/ui` here made loading the barrel pull in
 * the whole shared package, which re-enters `utils` before `apiUrl.ts` has
 * evaluated — so the binding was still in its temporal dead zone. React mounted,
 * rendered nothing, and issued no requests at all, with the error visible only in
 * the browser's own console.
 *
 * `@repo/ui` exports `"."` only (no subpath exports, and zero precedent for deep
 * importing it), so there is no narrow-import escape hatch the way there was for
 * VRG-604's `@infrastructure/services` cycle.
 *
 * It costs nothing to leave out: the sanitiser runs in `openSnackbar`, which
 * every toast passes through, so user-facing text is already filtered. This was
 * only the second line of defence, and a second line is not worth a cycle.
 *
 * The fallback below was still improved, which needs no import.
 */
export const getErrorMessage = (error: any) => {
  let message = "Something went wrong. Please try again.";

  if (error === null || error === undefined || error === "") {
    return message;
  }

  if (typeof error === "string") {
    message = error;
    return message;
  }

  // Axios / fetch errors: backend message lives in response.data.message.
  // Check this before error.message, which for axios is a generic
  // "Request failed with status code 4xx" string that hides the real reason.
  /*
    VRG-611 — the per-FIELD reason first, before `message`.

    A DTO rejected by the global ValidationPipe responds with the fixed string
    "Validation errors" (ERRORS[ErrorCode.DTO_VALIDATION_FAILED]) and puts the part
    worth reading in `details` ({[field]: string[]}, `formatValidationErrors`). Reading
    `message` first therefore shows a user the literal words "Validation errors", which
    names nothing they can act on — observed live under the code field on the phone
    enrolment modal.

    THIRD implementation of this extraction, hence hoisting it here: SignUpPage's local
    `extractMessage` and useAuthFactors' `extractApiErrorMessage` each do the same thing
    for the same reason, and every other caller of `getErrorMessage` was left exposed.
    Those two can now collapse onto this (§36); not done in the same commit so the
    behaviour change lands on its own.
  */
  const validationDetails = error?.response?.data?.details;
  if (validationDetails && typeof validationDetails === "object") {
    const fieldMessage = Object.values(validationDetails as Record<string, unknown>)
      .flat()
      .find((value): value is string => typeof value === "string" && value.length > 0);
    if (fieldMessage) return fieldMessage;
  }

  if (error?.response?.data?.message) {
    const backendMessage = error.response.data.message;
    // The generic validation envelope is never shown on its own — if `details` above
    // produced nothing usable, the caller's own fallback is better than these words.
    if (typeof backendMessage === "string" && backendMessage !== "Validation errors") {
      return backendMessage;
    }
    if (Array.isArray(backendMessage) && backendMessage.length > 0) {
      return typeof backendMessage[0] === "string"
        ? backendMessage[0]
        : (backendMessage[0][0] ?? message);
    }
  }

  if (!error.hasOwnProperty("message")) {
    return message;
  }

  if (typeof error.message === "string") {
    message = error.message;
    return message;
  }

  if (typeof error.message === "object") {
    message = error.message[0];
    return message;
  }

  if (typeof error.message[0] === "object") {
    message = error.message[0][0];
    return message;
  }

  return message;
};

export const getContrastColor = (color: string) => {
  const rgb = parseInt(color.slice(1), 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = (rgb >> 0) & 0xff;
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  return luma < 128 ? "#fff" : "#000";
};

/*
 * Parses Shopify global id
 * @param gid - Shopify global id
 * @returns - Shopify id
 * @example
 * parseShopifyGid('gid://shopify/Product/1234567890') => '1234567890'
 */
export const parseShopifyGid = (gid: string): string => {
  const match = gid.match(/\/(\d+)$/);
  return match ? match[1] : "";
};

export const toDollar = (amount: number, noDecimals = false) => {
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "usd",
    minimumFractionDigits: noDecimals ? 0 : 2,
    maximumFractionDigits: noDecimals ? 0 : 2,
  });
};

/**
 * Money that arrives from Stripe — plan `amount`, `unit_amount`, invoice
 * totals — is ALWAYS an integer number of cents (2900 = $29). This is the one
 * place that conversion happens.
 *
 * It used to be a per-file `normalizeAmount` copied into five billing surfaces
 * (§36: the second hand-roll is the trigger, and this was the fifth), which is
 * exactly the shape where a rounding or currency change gets made in three of
 * five places.
 *
 * `noDecimals` defaults to true: plan prices are whole dollars and render as
 * `$29`, not `$29.00`. Pass `false` where a fractional amount is possible.
 */
export const centsToDollar = (cents: number, noDecimals = true) => {
  return toDollar(cents / 100, noDecimals);
};
